import {
  Grid2 as Grid,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Typography,
  FormControl,
  Tooltip,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DataGrid } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import ClearIcon from "@mui/icons-material/Clear";
import { setPermitMapping } from "../../../store/actions/dataActions";

const toTitle = (value = "") =>
  value
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const PermitTypeLicenseMapping = ({
  title,
  permitMapping,
  setManagementData,
  isSm,
}) => {
  const dispatch = useDispatch();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedPrefix, setSelectedPrefix] = useState("");
  const [newPrefix, setNewPrefix] = useState("");
  const [addLoading, setAddLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    const clearTextInterval = setInterval(() => {
      if (errorText !== "") {
        setErrorText("");
      }
    }, 3000);

    return () => {
      clearInterval(clearTextInterval);
    };
  }, [errorText]);

  const rows = Object.entries(permitMapping || {}).map(
    ([permitType, licensePrefixes], index) => ({
      id: `${permitType}-${index}`,
      permitType,
      permitTypeLabel: toTitle(permitType),
      licensePrefixesArray: Array.isArray(licensePrefixes)
        ? licensePrefixes
        : [],
      licensePrefixes: Array.isArray(licensePrefixes)
        ? licensePrefixes.join(", ")
        : "",
    }),
  );

  const handleCellClick = (params) => {
    if (params.field !== "licensePrefixes") {
      return;
    }

    const cellData = {
      id: params.id,
      field: params.field,
      permitType: params.row.permitType,
      permitTypeLabel: params.row.permitTypeLabel,
      licensePrefixesArray: params.row.licensePrefixesArray,
    };

    setSelectedCell(cellData);
    setSelectedPrefix("");
    setNewPrefix("");
  };

  const handleSubmit = async (submitType) => {
    try {
      if (submitType === "add") {
        setAddLoading(true);
      } else {
        setRemoveLoading(true);
      }

      const payload = {
        dataObj: {
          submitType,
          permitType: selectedCell?.permitType,
          prefix: submitType === "add" ? newPrefix : selectedPrefix,
        },
        currentSession,
      };

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/permit-mapping-management`,
        payload,
        { withCredentials: true },
      );

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/data/managementData`,
        { currentSession: currentSession },
        { withCredentials: true },
      );

      setManagementData(res.data.data);
      dispatch(setPermitMapping(res?.data?.data?.permit_mapping || {}));
      setErrorText(resp.data.message);
      setSelectedPrefix("");
      setNewPrefix("");

      const refreshedRow = Object.entries(res?.data?.data?.permit_mapping || {})
        .map(([permitType, licensePrefixes]) => ({
          permitType,
          permitTypeLabel: toTitle(permitType),
          licensePrefixesArray: Array.isArray(licensePrefixes)
            ? licensePrefixes
            : [],
        }))
        .find((item) => item.permitType === selectedCell?.permitType);

      if (refreshedRow) {
        setSelectedCell({
          ...selectedCell,
          permitTypeLabel: refreshedRow.permitTypeLabel,
          licensePrefixesArray: refreshedRow.licensePrefixesArray,
        });
      }
    } catch (e) {
      setErrorText(e?.response?.data?.message || "Couldn't process data");
    } finally {
      setAddLoading(false);
      setRemoveLoading(false);
      setConfirmOpen(false);
    }
  };

  const renderSelectedValues = (selected) => {
    if (!Array.isArray(selected)) {
      selected = [selected];
    }

    if (selected.length === 0 || !selected[0]) {
      return <em>Select Prefix</em>;
    }

    return selected.join(", ");
  };

  const columns = [
    {
      field: "permitTypeLabel",
      headerName: "Permit Type",
      flex: 1,
      minWidth: 220,
      headerClassName: "management-datagrid-title",
    },
    {
      field: "licensePrefixes",
      headerName: "Allowed License Prefixes",
      flex: 2,
      minWidth: 420,
      headerClassName: "management-datagrid-title",
    },
  ];

  return (
    <Card sx={{ minHeight: "35rem" }}>
      <CardHeader title={title} />
      <CardContent>
        <DataGrid
          sx={{
            "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            "& .permit-mapping-selected-row": {
              backgroundColor: "rgba(0, 0, 0, 0.08)",
            },
            "& .permit-mapping-selected-row:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.12)",
            },
            minHeight: "24rem",
          }}
          rows={rows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 5,
              },
            },
          }}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          disableRowSelectionOnClick
          onCellClick={handleCellClick}
          getRowClassName={(params) =>
            params.row.permitType === selectedCell?.permitType
              ? "permit-mapping-selected-row"
              : ""
          }
          classes={{
            cell: "cell",
          }}
        />

        <Grid
          container
          spacing={isSm ? 1 : 2}
          alignItems="center"
          sx={{ mt: 1 }}
        >
          <Grid size={{ xs: 12 }}>
            <Typography sx={{ fontWeight: "bold" }}>
              {selectedCell
                ? `${selectedCell.permitTypeLabel} - Allowed License Prefixes`
                : "Select a row and click the Allowed License Prefixes cell"}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <FormControl fullWidth variant="filled" disabled={!selectedCell}>
              <Tooltip title="">
                <InputLabel
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {!selectedCell ? "Select Row First" : "Select Current Prefix"}
                </InputLabel>
              </Tooltip>
              <Select
                value={selectedPrefix}
                onChange={(e) => setSelectedPrefix(e.target.value)}
                renderValue={renderSelectedValues}
                endAdornment={
                  selectedPrefix ? (
                    <IconButton onClick={() => setSelectedPrefix("")}>
                      <ClearIcon />
                    </IconButton>
                  ) : undefined
                }
                IconComponent={selectedPrefix ? "div" : undefined}
                MenuProps={{
                  PaperProps: {
                    style: {
                      marginTop: "1.5px",
                      maxHeight: 150, // Set the desired max height for the dropdown menu
                      width: "auto",
                      maxWidth: "100%",
                    },
                  },
                }}
                sx={{
                  maxWidth: "100%", // Set the desired max-width for the input element
                  "& .MuiTypography-body2": {
                    whiteSpace: "wrap", // Prevent text from wrapping or shrinking
                  },
                }}
              >
                {(selectedCell?.licensePrefixesArray || []).map((item, i) => (
                  <MenuItem key={`${item}-${i}`} value={item}>
                    <Typography variant="body2">{item}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <LoadingButton
              loading={removeLoading}
              disabled={
                !selectedCell || !selectedPrefix || addLoading || removeLoading
              }
              fullWidth
              variant="contained"
              color="error"
              onClick={() => setConfirmOpen(true)}
            >
              Remove
            </LoadingButton>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <TextField
              disabled={!selectedCell}
              label={!selectedCell ? "Select Row First" : "Add New Prefix"}
              variant="filled"
              fullWidth
              value={newPrefix}
              onChange={(e) => setNewPrefix(e.target.value.toUpperCase())}
              InputProps={{
                endAdornment: newPrefix ? (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setNewPrefix("")}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <LoadingButton
              loading={addLoading}
              disabled={
                !selectedCell || !newPrefix || addLoading || removeLoading
              }
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => handleSubmit("add")}
            >
              Add
            </LoadingButton>
          </Grid>
        </Grid>

        <Typography
          variant="body1"
          color="primary"
          fontWeight="bold"
          textAlign="center"
          sx={{ mt: "1rem", minHeight: "1.5rem" }}
        >
          {errorText}
        </Typography>

        <Dialog
          open={confirmOpen}
          onClose={removeLoading ? () => {} : () => setConfirmOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm Prefix Removal</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to remove <strong>{selectedPrefix}</strong>{" "}
              from <strong>{selectedCell?.permitTypeLabel}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setConfirmOpen(false)}
              disabled={removeLoading}
              variant="outlined"
            >
              Cancel
            </Button>
            <LoadingButton
              onClick={() => handleSubmit("remove")}
              loading={removeLoading}
              variant="contained"
              color="error"
            >
              Confirm
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};
